/* global axios */
import { ref, onBeforeUnmount, watch } from 'vue';
import { startDiagnosticCapture } from './bugReportDiagnosticCapture';
import { useBugReportUpload } from './useBugReportUpload';
import { prepareBugReportVideo } from './prepareBugReportVideo';

const MAX_BYTES = 20 * 1024 * 1024;
export function useBugReportRecording(accountId, onRecording) {
  const { upload, uploadError, cancelUpload } = useBugReportUpload();
  const state = ref('idle');
  const error = ref(false);
  const preview = ref('');
  const events = ref([]);
  const attachedToken = ref('');
  const includeVideo = ref(true);
  const supported = Boolean(
    navigator.mediaDevices?.getDisplayMedia && window.MediaRecorder
  );
  let token;
  let endpoint;
  let stream;
  let recorder;
  let timer;
  let stopCapture;
  let video;
  let generation = 0;

  const release = () => {
    clearTimeout(timer);
    stream?.getTracks().forEach(track => track.stop());
    stream = null;
    if (stopCapture) events.value = stopCapture();
    stopCapture = null;
  };
  const discard = () => {
    cancelUpload();
    generation += 1;
    if (recorder?.state === 'recording') recorder.stop();
    release();
    if (preview.value) URL.revokeObjectURL(preview.value);
    preview.value = '';
    video = null;
    events.value = [];
    if (token) axios.delete(`${endpoint}/${token}`).catch(() => {});
    token = null;
    attachedToken.value = '';
    state.value = 'idle';
    onRecording(false);
  };
  const stop = () => {
    if (recorder?.state === 'recording') recorder.stop();
    release();
  };
  const start = async () => {
    discard();
    error.value = false;
    state.value = 'starting';
    const current = generation;
    const currentEndpoint = `/api/v1/accounts/${accountId.value}/bug_report_diagnostics`;
    try {
      // Invoke before awaiting network: browsers require a user gesture.
      const media = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
        selfBrowserSurface: 'include',
      });
      if (current !== generation) {
        media.getTracks().forEach(track => track.stop());
        return;
      }
      stream = media;
      const { data } = await axios.post(currentEndpoint);
      if (current !== generation) {
        axios.delete(`${currentEndpoint}/${data.token}`).catch(() => {});
        return;
      }
      endpoint = currentEndpoint;
      token = data.token;
      stopCapture = startDiagnosticCapture(accountId.value, token);
      const chunks = [];
      let size = 0;
      const mimeType = [
        'video/webm;codecs=vp8',
        'video/webm',
        'video/mp4',
      ].find(type => MediaRecorder.isTypeSupported(type));
      if (!mimeType) throw new Error('Unsupported recording format');
      recorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 800000,
      });
      recorder.ondataavailable = event => {
        if (current !== generation) return;
        size += event.data.size;
        if (size > MAX_BYTES) {
          error.value = true;
          stop();
        } else {
          chunks.push(event.data);
        }
      };
      recorder.onstop = async () => {
        if (current !== generation) return;
        release();
        state.value = 'processing';
        onRecording(false);
        try {
          if (!error.value && size > 0) {
            const prepared = await prepareBugReportVideo(
              new Blob(chunks, { type: recorder.mimeType })
            );
            if (current !== generation) return;
            if (prepared.size > MAX_BYTES)
              throw new Error('Recording too large');
            video = prepared;
            preview.value = URL.createObjectURL(video);
          }
        } catch {
          if (current !== generation) return;
          error.value = true;
        }
        state.value = 'review';
      };
      recorder.onerror = () => {
        error.value = true;
        stop();
      };
      stream
        .getVideoTracks()[0]
        .addEventListener('ended', stop, { once: true });
      recorder.start(1000);
      timer = setTimeout(stop, 120000);
      state.value = 'recording';
      onRecording(true);
    } catch {
      if (current !== generation) return;
      discard();
      error.value = true;
    }
  };
  const attach = async () => {
    const current = generation;
    state.value = 'uploading';
    error.value = false;
    const body = new FormData();
    body.append('events', JSON.stringify(events.value));
    if (includeVideo.value && video)
      body.append(
        'recording',
        video,
        video.type.includes('mp4') ? 'reproduction.mp4' : 'reproduction.webm'
      );
    try {
      const { data } = await upload(options =>
        axios.patch(`${endpoint}/${token}`, body, options)
      );
      if (current !== generation) return;
      attachedToken.value = data.token;
      events.value = data.events;
      state.value = 'attached';
    } catch {
      if (current !== generation) return;
      error.value = true;
      state.value = 'review';
    }
  };
  watch(accountId, discard, { flush: 'sync' });
  onBeforeUnmount(discard);
  return {
    uploadError,
    cancelUpload,
    state,
    error,
    preview,
    events,
    attachedToken,
    includeVideo,
    supported,
    start,
    stop,
    attach,
    discard,
  };
}
